import React, {
    PropsWithChildren,
    useEffect,
    useState,
    useCallback,
    FormEvent,
    useContext,
    SetStateAction,
    Dispatch,
} from 'react';
import {
    ComboBox,
    IComboBox,
    IComboBoxOption,
    SelectableOptionMenuItemType,
} from '@fluentui/react';
import { getCloudsList } from '../../AGSS/api/AGSSApi';
import { CancelToken } from 'axios';
import { usePromise } from '../hooks/usePromise';
import { ThemeContext } from '../../HubLayout/models/ThemeContext';
import {
    getThemeFromString,
    setSelectedEnvironmentNamesInLocalStorage,
} from '../util/localStorageUtils';
import { emitUserActionMetric } from '../util/metricsUtil';
import {
    ActionResult,
    FilterTypes,
    UserAction,
} from '../../Common/Enums/Metrics/MetricEnums';
import {
    componentComboBoxStyles,
    componentDivStyles,
} from '../util/CommonStyles';
import { SortSelectedOptions } from '../util/sortUtils';
import { comboBoxUtils } from '../util/comboBoxUtils';

interface IEnvironmentNameDropDownProps extends PropsWithChildren<any> {
    selectedEnvironments: string[];
    setSelectedEnvironments?: Dispatch<SetStateAction<string[]>>;
    setPageNumber?: Dispatch<SetStateAction<number>>;
    isLoaded: boolean;
    isMobile?: boolean;
    placeholder?: string;
}

export const CloudNameDropDown: React.FC<IEnvironmentNameDropDownProps> = (
    props: IEnvironmentNameDropDownProps
) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const [options, setOptions] = useState<IComboBoxOption[]>([]);
    const disabled = !props.isLoaded;

    const getData = useCallback(async (cancelToken?: CancelToken) => {
        return await getCloudsList(cancelToken);
    }, []);

    const [result, , dropdownLoaded] = usePromise(getData, true);

    const selectableOptions = options.filter(
        (option) =>
            (option.itemType === SelectableOptionMenuItemType.Normal ||
                option.itemType === undefined) &&
            !option.disabled
    );

    const sortedOptions = SortSelectedOptions(
        selectableOptions,
        props.selectedEnvironments,
        'Environment Names'
    );

    const onChange = (
        event: FormEvent<IComboBox>,
        option?: IComboBoxOption,
        index?: number,
        value?: string
    ) => {
        // Prevent the selection from being cleared when the combobox loses focus.
        // With`allowFreeform`, Fluent UI's ComboBox commits any pending typed
        // value on blur and routes that commit through onChange. We only care
        // about user-initiated selection changes, so ignore blur-driven calls.
        if (event?.type === 'blur') {
            return;
        }

        comboBoxUtils(
            option,
            props.selectedEnvironments,
            selectableOptions,
            props.setSelectedEnvironments!,
            props.setPageNumber,
            setSelectedEnvironmentNamesInLocalStorage,
            () =>
                emitUserActionMetric(
                    UserAction.DROPDOWN_FILTER,
                    FilterTypes.ENVIRONMENT_NAME,
                    ActionResult.SUCCESS,
                    process.env.REACT_APP_BUILD_VERSION
                )
        );
    };

    const divStyles = componentDivStyles(props.isMobile);

    const comboBoxStyles = componentComboBoxStyles(
        props.selectedEnvironments.length,
        theme,
        props.isMobile
    );

    useEffect(() => {
        if (dropdownLoaded && result) {
            const comboboxOptions: IComboBoxOption[] = [
                {
                    key: 'selectAll',
                    text: 'Select All',
                    itemType: SelectableOptionMenuItemType.SelectAll,
                },
                {
                    key: 'Header1',
                    text: 'Environment Clouds',
                    itemType: SelectableOptionMenuItemType.Header,
                },
                ...result.map((environment) => ({
                    key: environment.id.split('-')[1],
                    text: environment.id.split('-')[1],
                })),
            ];
            setOptions(comboboxOptions);
        }
    }, [result, dropdownLoaded]);

    return (
        <div style={divStyles}>
            {result && (
                <ComboBox
                    multiSelect
                    placeholder={props.placeholder || 'Environment Name'}
                    ariaLabel={props.placeholder || 'Environment Name'}
                    options={sortedOptions}
                    selectedKey={props.selectedEnvironments}
                    onChange={onChange}
                    styles={comboBoxStyles}
                    disabled={disabled}
                    allowFreeform
                />
            )}
        </div>
    );
};
